import React from 'react';
import styles from '../MagicSignTable.module.scss';
import { MagicSignRow, MagicSignStatus, SignStatus, ConfigMeta } from '../../../types';
import { MAGIC_SIGN_STATUS_CONFIG } from '../../../constants';
import LoadingSpinner from '../../../../_custom/LoadingSpinner/LoadingSpinner';
import Tooltip from '../../../../_custom/Tooltip/Tooltip';
import { copyIcon } from '../../../../../assets/hostedassets';

interface StatusCellProps {
    row: MagicSignRow;
    configMeta: ConfigMeta | null;
    onCopySigningLinks: (row: MagicSignRow) => void;
}

/** Hover card listing each signatory with a Signed/Pending pill (Figma: tooltip on Status). */
const SignatoryStatusPopover: React.FC<{ row: MagicSignRow }> = ({ row }) => {
    const signatories = row.signatories ?? [];
    if (signatories.length === 0) return null;
    return (
        <div className={styles.StatusPopover} role="tooltip">
            <div className={styles.StatusPopoverTitle}>Status</div>
            {signatories.map((s, i) => {
                const signed = s.signStatus === SignStatus.SIGNED;
                return (
                    <div key={`${s.email}-${i}`} className={styles.StatusPopoverRow}>
                        <span className={styles.StatusPopoverName}>{s.name}</span>
                        <span className={signed ? styles.SignedPill : styles.PendingPill}>{signed ? 'Signed' : 'Pending'}</span>
                    </div>
                );
            })}
        </div>
    );
};

const StatusCell: React.FC<StatusCellProps> = ({ row, configMeta, onCopySigningLinks }) => {
    const defaultCfg = MAGIC_SIGN_STATUS_CONFIG[row.status] ?? { label: row.status, color: '#555', bgColor: '#f5f5f5' };
    const overrideCfg = configMeta?.statusConfig?.[row.status];
    const cfg = overrideCfg ?? defaultCfg;

    // UPLOADING / GENERATING_LINKS: badge then spinner on the right (in-progress transient states)
    if (row.status === MagicSignStatus.UPLOADING || row.status === MagicSignStatus.GENERATING_LINKS) {
        return (
            <div className={styles.StatusCell}>
                <span className={styles.StatusBadge} style={{ color: cfg.color, backgroundColor: cfg.bgColor }}>
                    {cfg.label}
                </span>
                <LoadingSpinner height="1rem" color="#555" />
            </div>
        );
    }

    // GENERATED_LINKS: badge + small copy icon
    if (row.status === MagicSignStatus.GENERATED_LINKS) {
        return (
            <div className={styles.StatusCell}>
                <span className={styles.StatusBadge} style={{ color: cfg.color, backgroundColor: cfg.bgColor }}>
                    {cfg.label}
                </span>
                <button
                    className={styles.StatusCopyIconBtn}
                    title="Copy signing links"
                    aria-label="Copy signing links"
                    onClick={(e) => {
                        e.stopPropagation();
                        onCopySigningLinks(row);
                    }}
                >
                    <img src={copyIcon} alt="copy" className={styles.StatusCopyIcon} />
                </button>
            </div>
        );
    }

    // PARTIAL_SIGNED: badge + hover popover (via shared Tooltip) listing signatories
    if (row.status === MagicSignStatus.PARTIAL_SIGNED) {
        return (
            <Tooltip direction="top" usePortal flexible content={<SignatoryStatusPopover row={row} />}>
                <div className={styles.StatusCell}>
                    <span className={styles.StatusBadge} style={{ color: cfg.color, backgroundColor: cfg.bgColor }}>
                        {cfg.label}
                    </span>
                </div>
            </Tooltip>
        );
    }

    // Default: label badge only
    return (
        <div className={styles.StatusCell}>
            <span className={styles.StatusBadge} style={{ color: cfg.color, backgroundColor: cfg.bgColor }}>
                {cfg.label}
            </span>
        </div>
    );
};

export default StatusCell;
